import { useState, useEffect } from "react";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { DropdownItem } from "../ui/dropdown/DropdownItem";
import { Link } from "react-router-dom";
import { useSocket } from "../../context/SocketContext";
import firebaseNotificationService from "../../services/firebaseNotificationService";
import notificationService from "../../services/notification-service";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

export default function NotificationDropdown() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [notifying, setNotifying] = useState(true);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(false);
  const [fcmToken, setFcmToken] = useState(null);
  const [isRegisteringToken, setIsRegisteringToken] = useState(false);
  const { notifications, markNotificationAsRead } = useSocket();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  console.log(user, "this i id")

  const isPushSupported = 'Notification' in window && 'serviceWorker' in navigator;

  useEffect(() => {
    const setupPushNotifications = async () => {
      if (isAuthenticated && user && isPushSupported) {
        try {
          const permission = Notification.permission;

          const initialized = await firebaseNotificationService.initialize(user.id);
          
          if (initialized) {
            if (permission === 'default') {
              const success = await firebaseNotificationService.setupAndRegisterToken(user.id);
              
              if (success) {
                setPushNotificationsEnabled(true);
              } else {
                console.log('⚠️ Agent Portal - User needs to manually enable notifications');
              }
            } else if (permission === 'granted') {
              const token = await firebaseNotificationService.getFCMToken();
              
              if (token) {
                setFcmToken(token);
                setPushNotificationsEnabled(true);
                
                await registerPushToken(token);
              }
            }
          }

          firebaseNotificationService.setupForegroundHandler();
          
        } catch (error) {
          console.error('Agent Portal - Push notification setup error:', error);
        }
      }
    };

    setupPushNotifications();

    const handlePushNotification = (event) => {
      const pushNotification = {
        id: event.detail.data?.notificationId || `push_${Date.now()}`,
        title: event.detail.notification?.title || t('notificationDropdown.pushNotification'),
        description: event.detail.notification?.body || t('notificationDropdown.newNotification'),
        type: event.detail.data?.type || 'info',
        from: event.detail.data?.from || 'System',
        createdAt: new Date().toISOString(),
        isRead: false,
        data: event.detail.data,
        source: 'push'
      };
      
      console.log('Agent Portal - Displaying push notification:', pushNotification);
      
      if (window.showToast) {
        window.showToast({
          title: pushNotification.title,
          message: pushNotification.description,
          type: 'info'
        });
      }
    };
    
    window.addEventListener('agent-firebase-message', handlePushNotification);
    window.addEventListener('agent-new-push-notification', handlePushNotification);
    
    return () => {
      window.removeEventListener('agent-firebase-message', handlePushNotification);
      window.removeEventListener('agent-new-push-notification', handlePushNotification);
    };
  }, [isAuthenticated, user, isPushSupported, t]);

  const registerPushToken = async (token) => {
    if (!user || !user.id) return;
    
    setIsRegisteringToken(true);
    try {
      await notificationService.registerPushToken({
        userId: user.id,
        pushToken: token,
        platform: 'web',
        tokenType: 'fcm',
        fcmToken: token,
        deviceId: 'web-agent-' + navigator.userAgent.substring(0, 50),
        deviceName: navigator.userAgent,
        osName: navigator.platform,
        osVersion: navigator.userAgent,
        appName: 'Agent Portal',
        appVersion: '1.0.0'
      });
      
      console.log('Agent Portal - Push token registered successfully');
    } catch (error) {
      console.error('Agent Portal - Failed to register push token:', error);
    } finally {
      setIsRegisteringToken(false);
    }
  };

  const requestPushPermission = async () => {
    try {
      const permission = await firebaseNotificationService.requestPermission();
      
      if (permission === 'granted') {
        const token = await firebaseNotificationService.getFCMToken();
        if (token) {
          setFcmToken(token);
          setPushNotificationsEnabled(true);
          await registerPushToken(token);
        }
      }
    } catch (error) {
      console.error('Agent Portal - Error requesting push permission:', error);
    }
  };

  function toggleDropdown() {
    setIsOpen(!isOpen);
  }

  function closeDropdown() {
    setIsOpen(false);
  }

  const handleClick = () => {
    toggleDropdown();
    setNotifying(false);
  };

  const handleNotificationClick = (notification) => {
    markNotificationAsRead(notification.id);
    closeDropdown();
    
    if (notification.type === 'order') {
      window.location.href = '/orders';
    } else if (notification.type === 'product') {
      window.location.href = '/products';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return t('notificationDropdown.recently');
    
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return t('notificationDropdown.justNow');
    if (diffInMinutes < 60) return t('notificationDropdown.minutesAgo', { count: diffInMinutes });
    if (diffInMinutes < 1440) return t('notificationDropdown.hoursAgo', { count: Math.floor(diffInMinutes / 60) });
    return t('notificationDropdown.daysAgo', { count: Math.floor(diffInMinutes / 1440) });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order':
        return (
          <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-full dark:bg-primary-900">
            <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
        );
      case 'product':
        return (
          <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-full dark:bg-green-900">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full dark:bg-gray-800">
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  useEffect(() => {
    const unreadCount = notifications.filter(n => !n.isRead).length;
    setNotifying(unreadCount > 0);
  }, [notifications]);

  const getNotificationTitle = (notification) => {
    if (notification.title && typeof notification.title === 'object') {
      return notification.title.en || notification.title || t('notificationDropdown.notification');
    }
    return notification.title || notification.message || t('notificationDropdown.notification');
  };

  const getNotificationDescription = (notification) => {
    if (notification.description && typeof notification.description === 'object') {
      return notification.description.en || notification.description || '';
    }
    return notification.description || notification.message || '';
  };

  return (
    <div className="relative">
      <button
        className="relative flex items-center justify-center text-gray-500 transition-colors bg-white border border-gray-200 rounded-full dropdown-toggle hover:text-gray-700 h-11 w-11 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
        onClick={handleClick}
      >
        <span
          className={`absolute right-0 top-0.5 z-10 h-2 w-2 rounded-full bg-orange-400 ${
            !notifying ? "hidden" : "flex"
          }`}
        >
          <span className="absolute inline-flex w-full h-full bg-orange-400 rounded-full opacity-75 animate-ping"></span>
        </span>
        <svg
          className="fill-current"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.75 2.29248C10.75 1.87827 10.4143 1.54248 10 1.54248C9.58583 1.54248 9.25004 1.87827 9.25004 2.29248V2.83613C6.08266 3.20733 3.62504 5.9004 3.62504 9.16748V14.4591H3.33337C2.91916 14.4591 2.58337 14.7949 2.58337 15.2091C2.58337 15.6234 2.91916 15.9591 3.33337 15.9591H4.37504H15.625H16.6667C17.0809 15.9591 17.4167 15.6234 17.4167 15.2091C17.4167 14.7949 17.0809 14.4591 16.6667 14.4591H16.375V9.16748C16.375 5.9004 13.9174 3.20733 10.75 2.83613V2.29248ZM14.875 14.4591V9.16748C14.875 6.47509 12.6924 4.29248 10 4.29248C7.30765 4.29248 5.12504 6.47509 5.12504 9.16748V14.4591H14.875ZM8.00004 17.7085C8.00004 18.1228 8.33583 18.4585 8.75004 18.4585H11.25C11.6643 18.4585 12 18.1228 12 17.7085C12 17.2943 11.6643 16.9585 11.25 16.9585H8.75004C8.33583 16.9585 8.00004 17.2943 8.00004 17.7085Z"
            fill="currentColor"
          />
        </svg>
      </button>
      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className="absolute ltr:-right-[160px] rtl:-right-[160px] mt-[17px] flex w-[350px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark sm:w-[361px] lg:right-0"
      >
        <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100 dark:border-gray-700">
          <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            {t('notificationDropdown.notifications')}
            {notifications.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({t('notificationDropdown.newCount', { count: notifications.filter(n => !n.isRead).length })})
              </span>
            )}
          </h5>
          
          {isPushSupported && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">
                {pushNotificationsEnabled ? t('notificationDropdown.pushOn') : t('notificationDropdown.pushOff')}
              </span>
              {!pushNotificationsEnabled && Notification.permission === 'default' && (
                <button
                  onClick={requestPushPermission}
                  disabled={isRegisteringToken}
                  className="px-2 py-1 text-xs text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
                >
                  {isRegisteringToken ? t('notificationDropdown.enabling') : t('notificationDropdown.enablePush')}
                </button>
              )}
            </div>
          )}
        </div>
        
        <ul className="flex flex-col h-auto max-h-80 overflow-y-auto custom-scrollbar">
          {notifications.length === 0 ? (
            <li className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
              {t('notificationDropdown.noNotifications')}
            </li>
          ) : (
            notifications.slice(0, 10).map((notification) => (
              <li key={notification.id}>
                <DropdownItem
                  onItemClick={() => handleNotificationClick(notification)}
                  className={`flex gap-3 rounded-lg border-b border-gray-100 p-3 px-4.5 py-3 hover:bg-gray-100 dark:border-gray-800 dark:hover:bg-white/5 ${
                    !notification.isRead ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                >
                  {getNotificationIcon(notification.type)}
                  
                  <span className="block flex-1">
                    <span className="mb-1.5 block text-theme-sm text-gray-800 dark:text-gray-200 font-medium">
                      {getNotificationTitle(notification)}
                    </span>
                    {getNotificationDescription(notification) && (
                      <span className="mb-2 block text-sm text-gray-600 dark:text-gray-300">
                        {getNotificationDescription(notification)}
                      </span>
                    )}
                    <span className="flex items-center gap-2 text-gray-500 text-xs dark:text-gray-400">
                      <span className="capitalize">{t(`notificationDropdown.types.${notification.type || 'system'}`)}</span>
                      <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                      <span>{formatTime(notification.timestamp || notification.createdAt)}</span>
                      {notification.source === 'push' && (
                        <>
                          <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                          <span className="text-primary-500">{t('notificationDropdown.push')}</span>
                        </>
                      )}
                    </span>
                  </span>
                  
                  {!notification.isRead && (
                    <span className="w-2 h-2 bg-primary-500 rounded-full self-start mt-1"></span>
                  )}
                </DropdownItem>
              </li>
            ))
          )}
        </ul>
        
        {notifications.length > 0 && (
          <Link
            to="/"
            className="block px-4 py-2 mt-3 text-sm font-medium text-center text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            {t('notificationDropdown.viewAll')}
          </Link>
        )}
      </Dropdown>
    </div>
  );
}